import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { CollectionReptile, blankCollectionReptile } from "../collection/types/CollectionTypes.ts";
import { BasicFetch } from "../../utils/BasicFetch.ts";
import { ListingItem } from "../utilities/CollectionGroup.tsx";
import { CollectionReptileModal } from "../collection/modals/CollectionReptileModal.tsx";
import { Alert, Tabs, Tab, Row, Offcanvas, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faCircle, faInfoCircle, faPlus, faQuestion, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Gene } from "../../types/Gene.ts";
import { Morph } from "../../types/Morph.ts";
import { ModuleTitle } from "../utilities/ModuleTitle.tsx";
import { UserInfo } from "../../types/UserInfo.ts";
import { SelectCollectionReptileModal } from "../clutches/modals/SelectCollectionReptileModal.tsx";
import { EditListingModal } from "./EditListingModal.tsx";
import { driver } from "driver.js";
import { useRepToolContext } from "../../RepToolContext.tsx";
import { ShareAvailableReptilesModal } from '../collection/modals/ShareAvailableReptilesModal.tsx';
import { ToolMenuButton } from '../collection/Collection.tsx';
import { SubscriptionModal } from '../stripe/SubscriptionModal.tsx';

interface User {
  "id": number,
  "email": string,
  "first_name": string,
  "last_name": string
  "breeder_name": string,
  "scales": string,
}

const ReptileListingItem = ({ reptiles, canEdit = false, setEditingListingReptile, setViewingReptile }: { reptiles: CollectionReptile[], canEdit?: boolean, setEditingListingReptile: (reptile: CollectionReptile) => void, setViewingReptile: (reptile: CollectionReptile) => void }) => {

  return <>

    <div className="mt-4">
      <Row>
        {reptiles.map((reptile) => <ListingItem key={reptile.id}
          reptile={reptile}
          editListing={canEdit ? (reptile) => {
            setEditingListingReptile(reptile);
          } : undefined}
          openDetailsModal={(reptile => {
            setViewingReptile(reptile);
          })} />)}
      </Row>
    </div>
  </>
}

export const AvailableList = ({ userId, backToMenu, currentUser }: { userId: string | null, backToMenu?: () => void, currentUser?: UserInfo }) => {
  const [fetching, setFetching] = useState(true);
  const [availableReptiles, setAvailableReptiles] = useState<CollectionReptile[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [availableReptilesVisible, setAvailableReptilesVisible] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingListingReptile, setEditingListingReptile] = useState<CollectionReptile | null>(null);
  const [viewingReptile, setViewingReptile] = useState<CollectionReptile | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [viewAsCustomer, setViewAsCustomer] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [allGenes, setAllGenes] = useState<Gene[]>([]);
  const [allMorphs, setAllMorphs] = useState<Morph[]>([]);
  const {appendControls} = useRepToolContext();

  useEffect(() => {
    if ((window as any).allMorphs || (window as any).allGenes) {
      return;
    }
    BasicFetch({
      url: `${(window as any).serverLocation}morph/get`,
      method: 'GET',
      onSuccess: (data) => {
        setAllMorphs(data.morphs);
        setAllGenes(data.genes);
        (window as any).allMorphs = data.morphs;
        (window as any).allGenes = data.genes;
      }
    })
  }, []);


  const isOwner = useMemo(() => {
    return currentUser && userId && currentUser.id === userId;
  }, [currentUser, userId]);

  const isSharing = isOwner && availableReptilesVisible;

  const getReptiles = (editingListingReptile?: CollectionReptile) => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/${userId}/available_data`,
      onSuccess: ({ reptiles, user, availableReptilesVisible }) => {
        setAvailableReptiles(reptiles);
        setUser(user);
        setAvailableReptilesVisible(availableReptilesVisible);
        if (editingListingReptile) {
          const reptile = reptiles.find((r: CollectionReptile) => r.id === editingListingReptile.id);
          if (reptile) {
            setEditingListingReptile(reptile);
          } else {
            setEditingListingReptile(null);
          }
        }
      },
      onFail: () => {
        (window as any).alert("Failed to get available reptiles, please try again later.")
      }, onFinish: () => {
        setFetching(false);
      }
    })
  }

  useEffect(() => {
    if (fetching && userId) {
      getReptiles();
    }
  }, [fetching, userId]);

  const pageTitle = useMemo(() => {
    let name = user?.breeder_name;
    if (name?.endsWith('s')) {
      return `${name}' Available Animals`;
    }
    return `${name}'s Available Animals`;
  }, [user]);

  const setControls = () => {
    const availableDriverObj = driver({
      steps: [
        { popover: { title: 'Welcome to Your Store', description: 'This is your store, here you can sell your available animals.' } },
        { element: '#add-to-store', popover: { title: 'Add to Your Store', description: 'To add animals to your store simply click this "+" icon here. It will pull up a popup window so that you can select an animal from your collection and mark it as "Available" which will make it visible to other users here.' } },
        { element: '#additional-tools', popover: { title: 'Additional Tools', description: 'Click here to access additional tools such as sharing your store with other users or viewing your store as a customer.' } },
      ]
    });

    appendControls(
      <div className='d-flex flex-row align-items-between w-100'>
        <div>
          {isOwner && !viewAsCustomer && <div onClick={() => setShowShareModal(true)} className="d-flex my-auto align-items-center">
            <FontAwesomeIcon className={`mr-2 ${isSharing ? 'text-success' : 'text-warning'}`} icon={faCircle} />
            {isSharing ? 'Visible to Other Users' : 'Hidden to Other Users'}
          </div>}
          {isOwner && viewAsCustomer && 'Viewing as Customer'}
        </div>
        <div className="ml-auto">
          {isOwner && !viewAsCustomer && <>
            <span id='additional-tools' className="mr-4" onClick={() => {
              setShowMenu(true);
            }}>
              <FontAwesomeIcon icon={faBars} size='xl' />
            </span>

            <span className='mr-4' onClick={() => {
              availableDriverObj.drive();
            }}>
              <FontAwesomeIcon icon={faQuestion} size='xl' />
            </span>
            <span id='add-to-store' onClick={() => setShowAddModal(true)}>
              <FontAwesomeIcon icon={faPlus} size='xl' />
            </span>
          </>}
          {isOwner && viewAsCustomer && <span className='ml-4'>
            <Button onClick={() => setViewAsCustomer(false)} size='sm' variant="secondary">Exit Customer View</Button>
          </span>}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (currentUser && currentUser.id === userId && !showAddModal && !editingListingReptile && !showShareModal && !openSubscriptionModal && !viewingReptile && !viewAsCustomer) {
      setControls();
    }
  }, [showAddModal, editingListingReptile, showShareModal, openSubscriptionModal, currentUser, userId, isOwner, availableReptilesVisible, viewAsCustomer]);

  const allReptiles = useMemo(() => {
    // group reptiles by reptile name
    let reptileGroups: { [key: string]: CollectionReptile[] } = {};
    availableReptiles.forEach((reptile) => {
      if (!reptileGroups[reptile.reptile_name]) {
        reptileGroups[reptile.reptile_name] = [];
      }
      reptileGroups[reptile.reptile_name].push(reptile);
    });
    if (!activeTab) {
      setActiveTab(Object.keys(reptileGroups)[0]);
    }
    return reptileGroups;
  }, [availableReptiles]);

  const onSelectReptile = (reptile: CollectionReptile) => {
    setEditingListingReptile(reptile);
    setShowAddModal(false);
    if (reptile.status === 'available') {
      getReptiles();
      return;
    }

    const url = `${(window as any).serverLocation}collection_reptile/move/current/${reptile.status}/destination/available`;
    BasicFetch({
      url,
      data: {
        reptiles: [reptile],
        reptile: reptile.reptile,
      },
      method: 'POST',
      onSuccess: (data) => {
        getReptiles();
        alert(`Successfully moved ${reptile.name} to available.`);
      },
      onFail: () => {
        alert("There was an issue moving the reptile to available. Please try again later.");
      },
    })
  }
  // only display this many tabs at once for smaller screens
  const displayTabSizes = {
    'sm': 3,
    'md': 4,
    'lg': 5,
    'xl': 6
  }
  // if there are 3 differet reptiles hide the tabs and show a select dropdown instead
  const sizeToShowSelect = useMemo(() => {
    let size = '-xl';
    if (availableReptiles.length <= displayTabSizes.sm) {
      size = '';
    } else if (availableReptiles.length <= displayTabSizes.md) {
      size = '-md';
    } else if (availableReptiles.length <= displayTabSizes.lg) {
      size = '-lg';
    }
    return size;


  }, [availableReptiles.length]);

  return <div className="mt-4 px-2 pb-5">
    {currentUser && <Offcanvas className='bg-black border-right px-4 text-light' show={showMenu} onHide={() => setShowMenu(false)}>
      <Offcanvas.Header className='text-light d-flex justify-content-end' >
        <FontAwesomeIcon onClick={() => { setShowMenu(false); }} icon={faTimes} size='2x' />
      </Offcanvas.Header>

      <div className='d-flex flex-column mt-3'>
        <h5>Tools</h5>
      </div>
      <div>
        <ToolMenuButton userInfo={currentUser} clickFunction={() => {
          if (currentUser.subscription?.subscription_type !== "FREE") {
            setShowShareModal(true);
          } else {
            setOpenSubscriptionModal(true);
          }
          setShowMenu(false);
        }}>
          Share My Store
        </ToolMenuButton>
        <ToolMenuButton userInfo={currentUser} clickFunction={() => { setViewAsCustomer(true); setShowMenu(false) }}>
          View As Customer
        </ToolMenuButton>
      </div>
    </Offcanvas>}
    {!fetching && <ModuleTitle title={pageTitle} backToMenu={backToMenu} />}
    {showAddModal && <SelectCollectionReptileModal onSelect={(reptile) => {
      onSelectReptile(reptile)
    }} 
    closeModal={() => setShowAddModal(false)} 
    genes={allGenes} 
    morphs={allMorphs}
    onCollectionAdd={getReptiles}
    allReptiles />}
    {openSubscriptionModal && currentUser && <SubscriptionModal displayFeatureRequiresMessage={true} userInfo={currentUser} closeModal={() => setOpenSubscriptionModal(false)} />}

    {showShareModal && <ShareAvailableReptilesModal
      closeModal={(sharingTurnedOn) => {
        setShowShareModal(false);
        setAvailableReptilesVisible(sharingTurnedOn);
      }}
    />}
    {editingListingReptile && <EditListingModal
      closeModal={(sharingTurnedOn) => {
        setEditingListingReptile(null);
        setAvailableReptilesVisible(sharingTurnedOn);
      }}
      onFinish={() => {
        setEditingListingReptile(null);
        // pass undefined so that getReptiles doesn't try to set the editingListingReptile
        // to the reptile that was just edited and then close the modal
        getReptiles(undefined);
      }}
      reptile={editingListingReptile}
    />}
    {!!viewingReptile && <CollectionReptileModal
      closeModal={() => { setViewingReptile(null); setControls(); }}
      reptileId={viewingReptile.uuid}
      readOnly={!isOwner || viewAsCustomer}
      showInquiry={!isOwner || viewAsCustomer}
      isSpecies={viewingReptile.is_species}
      genes={allGenes}
      morphs={allMorphs}
      activePanel={viewAsCustomer || !isOwner ? 0 : 2}
    />}
    <div>
      {availableReptiles.length > 0 && activeTab && <div className={`d${sizeToShowSelect}-block d-none`}>
        <Tabs defaultActiveKey={activeTab} className="w-100">
          {Object.keys(allReptiles).map((reptileName) => <Tab key={reptileName} eventKey={reptileName} title={reptileName}>
            <ReptileListingItem reptiles={allReptiles[reptileName]} canEdit={!!isOwner && !viewAsCustomer} setEditingListingReptile={setEditingListingReptile} setViewingReptile={setViewingReptile} />
          </Tab>)}
        </Tabs>
      </div>}
      {availableReptiles.length > 0 && activeTab && <div className={`d${sizeToShowSelect}-none`}>
        <select className="form-select bg-dark text-light w-100" value={activeTab} onChange={(e) => setActiveTab(e.target.value)}>
          {Object.keys(allReptiles).map((reptileName) => <option key={reptileName} value={reptileName}>{reptileName}</option>)}
        </select>
        {
          allReptiles[activeTab].length > 0 &&
          <ReptileListingItem reptiles={allReptiles[activeTab]} canEdit={!!isOwner && !viewAsCustomer} setEditingListingReptile={setEditingListingReptile} setViewingReptile={setViewingReptile} />
        }
      </div>}


      {availableReptiles.length === 0 && !fetching && availableReptilesVisible && <Alert className="w-100" variant="info">
        <div className="d-flex w-100">
          <FontAwesomeIcon className="my-auto" size="lg" icon={faInfoCircle} />
          <h5 className="text-info ml-4 my-auto">No available animals</h5>
        </div>
      </Alert>}
      {!fetching && !availableReptilesVisible && !isOwner && <Alert className="w-100" variant="info">
        <div className="d-flex w-100">
          <FontAwesomeIcon className="my-auto" size="lg" icon={faInfoCircle} />
          <h5 className="text-info ml-4 my-auto">
            {user?.breeder_name} has not made their available animals visible.
          </h5>
        </div>
      </Alert>}
      {fetching && availableReptiles.length === 0 &&
        <div className="mt-4">
          <Row>
            {Array.from({ length: 8 }).map((_, index) => <ListingItem key={index} reptile={blankCollectionReptile} loading={true} />)}
          </Row>
        </div>
      }
    </div>
  </div>
}

(window as any).createAvailableView = ({ userId }: { userId: string | null }) => {
  ReactDOM.render(<AvailableList userId={userId} />, document.getElementById('app'));
}